/**
 * A week of opening hours, as a person edits it (Phase 141).
 *
 * The stored shape carries both: a detailed `windows` list, and the three summary fields every
 * older row has and every one-line display reads. These functions keep the two agreeing, which is
 * the only thing that can go quietly wrong here — a summary that says 9–18 for a desk that closes
 * at one on Saturdays is worse than no summary.
 */

export interface DayWindow {
  /** 0 is Sunday. */
  day: number;
  startMinute: number;
  endMinute: number;
}

export interface WeekHours {
  startMinute: number;
  endMinute: number;
  days: number[];
  timeZone: string;
  windows?: DayWindow[];
}

export const DAY_LABELS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/** Minutes from midnight as `HH:MM`, which is what a time input speaks. */
export function toTime(minutes: number): string {
  const safe = Math.max(0, Math.min(24 * 60, Math.round(minutes)));

  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
}

export function toMinutes(value: string): number {
  const [hours = '0', minutes = '0'] = value.split(':');

  return Math.max(0, Math.min(24 * 60, Number(hours) * 60 + Number(minutes)));
}

/**
 * The week as windows, whatever shape it arrived in.
 *
 * A row saved before the detailed week existed has only the single window, and it means the same
 * thing repeated across its days — so it is expanded rather than special-cased everywhere.
 */
export function windowsOf(hours: WeekHours): DayWindow[] {
  if (hours.windows && hours.windows.length > 0) return [...hours.windows];

  return hours.days.map((day) => ({
    day,
    startMinute: hours.startMinute,
    endMinute: hours.endMinute,
  }));
}

/**
 * The summary fields, derived from the windows.
 *
 * The earliest opening and the latest closing of the week: it is what a single line can honestly
 * say about a week with a lunch break in it, and it keeps the stored row valid for every reader
 * written before the detailed week existed.
 */
export function summarise(windows: DayWindow[], timeZone: string): WeekHours {
  const usable = windows.filter((window) => window.endMinute > window.startMinute);

  if (usable.length === 0) {
    return { startMinute: 0, endMinute: 0, days: [], timeZone, windows: [] };
  }

  return {
    startMinute: Math.min(...usable.map((window) => window.startMinute)),
    endMinute: Math.max(...usable.map((window) => window.endMinute)),
    days: [...new Set(usable.map((window) => window.day))].sort((a, b) => a - b),
    timeZone,
    windows: [...usable].sort((left, right) =>
      left.day === right.day ? left.startMinute - right.startMinute : left.day - right.day,
    ),
  };
}

/** The windows of one day, in order. */
export function windowsOn(windows: DayWindow[], day: number): DayWindow[] {
  return windows
    .filter((window) => window.day === day)
    .sort((left, right) => left.startMinute - right.startMinute);
}

export type WeekProblem = 'backwards' | 'overlap';

/**
 * What is wrong with a week somebody is editing.
 *
 * Overlap is worth refusing rather than merging: two windows that overlap mean somebody typed one
 * of them wrong, and silently joining them hides which.
 */
export function problemsWithWeek(windows: DayWindow[]): WeekProblem | null {
  if (windows.some((window) => window.endMinute <= window.startMinute)) return 'backwards';

  for (let day = 0; day < 7; day += 1) {
    const today = windowsOn(windows, day);

    for (let index = 1; index < today.length; index += 1) {
      if (today[index]!.startMinute < today[index - 1]!.endMinute) return 'overlap';
    }
  }

  return null;
}
